"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import Image from "next/image"
import { useTheme } from "@mui/material/styles"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import Chip from "@mui/material/Chip"
import Container from "@mui/material/Container"
import Grid from "@mui/material/Grid"
import useMediaQuery from "@mui/material/useMediaQuery"
import { ExternalLink } from "lucide-react"
import { useTrackInteraction } from "@/app/hooks/useTrackInteraction"

// Define a type for partner objects
interface Partner {
  id: string
  name: string
  logo: string
  url: string
  initials: string
  category: string
}

// Partner data with name, logo, url, and category
const partners: Partner[] = [
  {
    id: "director-nyx",
    name: "Director NYX",
    logo: "/images/guia-kinky-logo/nyx.jpg",
    url: "https://www.directornyx.com",
    initials: "NYX",
    category: "Foto & Vídeo",
  },
  {
    id: "chastity",
    name: "Chastity",
    logo: "/images/guia-kinky-logo/chastity.jpg",
    url: "https://www.chastity.com.br/",
    initials: "CH",
    category: "Sex Shop",
  },
  {
    id: "cafes-secrets",
    name: "Café Secrets",
    logo: "/images/guia-kinky-logo/cafe-secrets.jpg",
    url: "https://www.instagram.com/cafesecretsoficial/",
    initials: "CS",
    category: "Café",
  },
  {
    id: "masmorra-velas",
    name: "Masmorra Velas",
    logo: "/images/guia-kinky-logo/masmorra-velas.jpg",
    url: "https://loja.masmorravelas.com.br/",
    initials: "MV",
    category: "Acessórios",
  },
]

export function AppFooterGuia() {
  const theme = useTheme()
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [touchedId, setTouchedId] = useState<string | null>(null)
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const isExtraSmall = useMediaQuery("(max-width:360px)")
  const { trackInteraction } = useTrackInteraction()

  // Clear touch state after a delay
  useEffect(() => {
    if (touchedId !== null) {
      const timer = setTimeout(() => {
        setTouchedId(null)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [touchedId])

  // Handle touch events for mobile
  const handleTouchStart = (id: string) => {
    setTouchedId(id)
  }

  // Handle click on partner link
  const handlePartnerClick = (partnerId: string) => {
    trackInteraction(partnerId, "profile_view", "banner")
  }

  // Calculate avatar size based on screen size
  const avatarSize = isExtraSmall ? 50 : isMobile ? 60 : 80

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: theme.palette.background.default,
        paddingTop: 2.5,
        paddingBottom: 2.5,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: isMobile ? theme.spacing(0, 1) : theme.spacing(0, 2),
        }}
      >
        <Grid container spacing={isExtraSmall ? 1 : isMobile ? 2 : 1} justifyContent="center" alignItems="center">
          {partners.map((partner) => (
            <Grid
              item
              xs={4}
              sm={4}
              md={2.4}
              key={partner.id}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: isExtraSmall ? 1 : undefined,
              }}
            >
              <Link
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
                onClick={() => handlePartnerClick(partner.id)}
              >
                <Box
                  sx={{
                    position: "relative",
                    marginBottom: theme.spacing(1),
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                  onMouseEnter={() => setHoveredId(partner.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onTouchStart={() => handleTouchStart(partner.id)}
                >
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: "50%",
                      padding: "2px",
                      background: theme.palette.background.default,
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        borderRadius: "50%",
                        padding: "2px",
                        background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        backgroundSize: "200% 200%",
                        opacity: touchedId === partner.id || hoveredId === partner.id ? 0.8 : 0,
                        transition: "opacity 0.3s ease",
                        zIndex: 0,
                        margin: "-2px",
                        filter: "blur(0.5px)",
                        animation:
                          touchedId === partner.id || hoveredId === partner.id ? "spin 6s linear infinite" : "none",
                      },
                      "&:hover::before": {
                        opacity: 0.8,
                        animation: "spin 6s linear infinite",
                      },
                      "&:hover": {
                        boxShadow: `0 0 12px ${theme.palette.secondary.main}40`,
                      },
                      ...(touchedId === partner.id && {
                        boxShadow: `0 0 12px ${theme.palette.secondary.main}40`,
                      }),
                    }}
                  >
                    <Box
                      sx={{
                        width: avatarSize,
                        height: avatarSize,
                        borderRadius: "50%",
                        overflow: "hidden",
                        border: `2px solid ${theme.palette.background.default}`,
                        zIndex: 1,
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: theme.palette.background.paper,
                      }}
                    >
                      <Image
                        src={partner.logo || "/placeholder.svg"}
                        alt={`${partner.name} logo`}
                        width={avatarSize}
                        height={avatarSize}
                        style={{
                          objectFit: "cover",
                          width: "100%",
                          height: "100%",
                        }}
                        // Use loading="lazy" instead of eager to avoid preload warnings
                        loading="lazy"
                      
                        // Only priority for the first two items
                        priority={false}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(0,0,0,0.5)",
                          opacity: 0,
                          transition: "opacity 0.3s",
                          "&:hover": {
                            opacity: 1,
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#fff",
                            fontWeight: "bold",
                          }}
                        >
                          {partner.initials}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  {(hoveredId === partner.id || touchedId === partner.id) && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -4,
                        right: -4,
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: theme.palette.primary.main,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 2,
                      }}
                    >
                      <ExternalLink size={12} color="#000" />
                    </Box>
                  )}
                </Box>
              </Link>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: isExtraSmall ? "0.75rem" : undefined,
                    whiteSpace: "wrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "100%",
                  }}
                >
                  {partner.name}
                </Typography>
                <Chip
                  label={partner.category}
                  variant="outlined"
                  size="small"
                  sx={{
                    marginTop: theme.spacing(0.5),
                    borderColor: `${theme.palette.secondary.main}40`,
                    color: theme.palette.secondary.main,
                    fontSize: isExtraSmall ? "0.65rem" : "0.75rem",
                    height: isExtraSmall ? 20 : 24,
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      padding: isExtraSmall ? "0 6px" : "0 8px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}
